import { PageTitle } from '@/components/custom/PageTitle'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import { PlanSelection } from './components/PlanSelection'
import { InvoiceHistory } from './components/InvoiceHistory'
import { OneTimeFees } from './components/OneTimeFees'
import { CurrentPlan } from './components/CurrentPlan'
import { BillingPortalButton } from './components/BillingPortalButton'
import { getInvoices, getOneTimeFees, getSubscription } from '@/services/billingService'
import { useState, useEffect } from 'react'

export const Billing = () => {
  const dispatch: AppDispatch = useDispatch()
  const { userData, token } = useSelector((state: RootState) => state.user)
  const { subscription: currentSubscription, invoices, oneTimeFees } = useSelector((state: RootState) => state.billing)
  const [isLoadingOneTimeFees, setIsLoadingOneTimeFees] = useState(false)
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)

  // Check if user has admin role for billing access
  const hasAdminAccess = userData?.role === 'admin' || userData?.isTCSAdmin
  const currentOrgId = userData?.currentOrganization?.id

  const loadBillingData = () => {
    if (token && currentOrgId) {
      dispatch(getSubscription(token, currentOrgId))
      dispatch(getOneTimeFees(token, currentOrgId))
      dispatch(getInvoices(token, currentOrgId))
    }
  }

  useEffect(() => {
    if (token && currentOrgId) {
      dispatch(getSubscription(token, currentOrgId))
      dispatch(getOneTimeFees(token, currentOrgId))
      dispatch(getInvoices(token, currentOrgId))
    }
  }, [token, currentOrgId, dispatch])

  const onRefreshOneTimeFees = async () => {
    if (token && currentOrgId) {
      setIsLoadingOneTimeFees(true)
      await dispatch(getOneTimeFees(token, currentOrgId, false))
      setIsLoadingOneTimeFees(false)
    }
  }

  const onRefreshInvoices = async () => {
    if (token && currentOrgId) {
      setIsLoadingInvoices(true)
      await dispatch(getInvoices(token, currentOrgId, false))
      setIsLoadingInvoices(false)
    }
  }

  if (!hasAdminAccess) {
    return (
      <div className="protectedPageLayout">
        <PageTitle title="Billing" />
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Access Denied
          </h2>
          <p className="text-red-600">
            Only organization administrators can access billing information. Please contact your organization admin for billing-related inquiries.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="protectedPageLayout mx-auto">
      <PageTitle title="Billing Management" hasSubtitle />
      <p className="text-secondary font-medium text-xl mb-10">
        Manage your subscription, view invoices, and update payment information
      </p>

      <div className="mt-8 w-full max-w-2xl rounded-lg border bg-white shadow-sm overflow-hidden divide-y divide-border">
        <CurrentPlan
          subscription={currentSubscription}
          token={token ?? undefined}
          organizationId={currentOrgId ?? undefined}
        />
        {token && currentOrgId && (
          <PlanSelection
            token={token}
            organizationId={currentOrgId}
            currentSubscription={currentSubscription}
            onPlanSelected={loadBillingData}
          />
        )}
        {token && currentOrgId && currentSubscription && (
          <BillingPortalButton
            token={token}
            organizationId={currentOrgId}
          />
        )}
        <OneTimeFees
          fees={oneTimeFees}
          onRefresh={onRefreshOneTimeFees}
          isLoading={isLoadingOneTimeFees}
        />
        <InvoiceHistory
          invoices={invoices}
          onRefresh={onRefreshInvoices}
          isLoading={isLoadingInvoices}
        />
      </div>
    </div>
  )
}
